import { type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Icon, IconButton } from '@socialchorus/shared-ui-components';
import type { ContentLink } from '../../../../models/user-generated-contents/types';
import { MAX_LINKS } from './use-link-uploader';
import {
  useSortableItem,
  SortableGrid,
  type DragHandleProps,
} from './sortable';
import './links.scss';

const linkId = (link: ContentLink, index: number): string =>
  link.url ? `${link.url}-${index}` : `link-${index}`;

type LinkFaviconProps = {
  link: ContentLink;
  size: number;
};

const LinkFavicon = ({ link, size }: LinkFaviconProps) => {
  if (link.image_url) {
    return (
      <img
        src={link.image_url}
        alt=""
        className="ugc-links__favicon-img"
        width={size}
        height={size}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }
  return (
    <span className="ugc-links__favicon-fallback" aria-hidden="true">
      <Icon size={Math.round(size * 0.7)}>language</Icon>
    </span>
  );
};

type SortableLinkItemProps = {
  link: ContentLink;
  index: number;
  onDelete: (index: number) => void;
};

const SortableLinkItem = ({ link, index, onDelete }: SortableLinkItemProps) => {
  const { t } = useTranslation();
  const { setNodeRef, style, attributes, listeners } = useSortableItem(
    linkId(link, index)
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="ugc-links__item"
      {...attributes}
      {...listeners}
    >
      <div className="ugc-links__item-favicon">
        <LinkFavicon link={link} size={48} />
      </div>
      <div className="ugc-links__item-body">
        <div className="ugc-links__item-title">{link.title || link.url}</div>
        {link.description && (
          <div className="ugc-links__item-description">{link.description}</div>
        )}
      </div>
      <div className="ugc-links__item-actions">
        <IconButton
          iconName="delete"
          size="compact"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onDelete(index);
          }}
          onKeyDown={(e: KeyboardEvent) => e.stopPropagation()}
          aria-label={t('user_generated_content.media.links.delete')}
          className="bg-black-05"
        />
      </div>
    </div>
  );
};

type LinksProps = {
  links: ContentLink[];
  totalCount: number;
  onDelete: (index: number) => void;
  onReorder: (links: ContentLink[]) => void;
  dragHandleProps?: DragHandleProps;
  // True while this group is being dragged / keyboard-selected
  isDragging?: boolean;
};

const Links = ({
  links,
  totalCount,
  onDelete,
  onReorder,
  dragHandleProps,
  isDragging,
}: LinksProps) => {
  const { t } = useTranslation();

  return (
    <div className="ugc-links__section">
      <div
        className={`ugc-links__section-header${
          dragHandleProps ? ' ugc-links__section-header--draggable' : ''
        }${isDragging ? ' ugc-links__section-header--dragging' : ''}`}
        {...dragHandleProps}
      >
        {dragHandleProps && (
          <Icon
            className={`ugc-links__drag-handle${
              isDragging ? ' ugc-links__drag-handle--active' : ''
            }`}
            size={20}
            aria-hidden
          >
            drag_indicator
          </Icon>
        )}
        <Badge
          text={t('user_generated_content.media.links.section_label', {
            count: totalCount,
            max: MAX_LINKS,
          })}
        />
      </div>
      <SortableGrid
        items={links}
        getId={(l) => linkId(l, links.indexOf(l))}
        onReorder={(reordered) =>
          onReorder(
            reordered.map((l: ContentLink, i: number) => ({ ...l, order: i }))
          )
        }
      >
        <div className="ugc-links__layout">
          {links.map((link, i) => (
            <SortableLinkItem
              key={linkId(link, i)}
              link={link}
              index={i}
              onDelete={onDelete}
            />
          ))}
        </div>
      </SortableGrid>
    </div>
  );
};

export default Links;
